"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useState } from "react";
import { ArrowUpLeft, BadgeCheck, Check, Copy, FileText, Sparkles } from "lucide-react";
import { Panel, type Fold } from "@/features/auctions/v2/single/AuctionFacts";
import FitHex from "@/features/collections/detail/FitHex";
import { Avatar } from "@/features/home-v2/ui";
import type { NftView } from "./nftView";
import { withPercent } from "@/lib/percent";
const tr = (isFa: boolean) => (fa: string, en: string) => isFa ? fa : en;

/** Chain · standard — the chips over the name. */
export function NftMeta(t0) {
  const $ = _c(9);
  const {
    v
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="v2-chip">NFT</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== v.network.display || $[2] !== v.network.raw) {
    t2 = v.network.display ? <span className="v2-chip v2-chip--chain" data-chain={v.network.raw.toLowerCase()}>{v.network.display}</span> : null;
    $[1] = v.network.display;
    $[2] = v.network.raw;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== v.standard) {
    t3 = v.standard ? <span className="v2-chip"><bdi>{v.standard}</bdi></span> : null;
    $[4] = v.standard;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="ap-meta">{t1}{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/** The name, with the token number beside it when the name does not already end with it. */
export function NftTitle(t0) {
  const $ = _c(7);
  const {
    v
  } = t0;
  let t1;
  if ($[0] !== v.name) {
    t1 = <bdi>{v.name}</bdi>;
    $[0] = v.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== v.tokenBadge) {
    t2 = v.tokenBadge ? <span className="ns-token"><bdi>{v.tokenBadge}</bdi></span> : null;
    $[2] = v.tokenBadge;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <h1 className="ns-title">{t1}{t2}</h1>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/** The collection it belongs to — its page (on this NFT's network) when the payload names it. */
export function NftCollection(t0) {
  const $ = _c(16);
  const {
    v
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== v.collection || $[1] !== v.lang) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const t = tr(v.lang === "fa");
      if (!v.collection) {
        t3 = null;
        break bb0;
      }
      t1 = "ap-work ns-coll";
      t2 = t("\u06A9\u0627\u0644\u06A9\u0634\u0646", "Collection");
    }
    $[0] = v.collection;
    $[1] = v.lang;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[5] !== v.collection.name) {
    t4 = <bdi>{v.collection.name}</bdi>;
    $[5] = v.collection.name;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <BadgeCheck aria-hidden={true} className="ns-verified" />;
    t6 = <ArrowUpLeft aria-hidden={true} />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== v.collection.href) {
    t7 = <Link href={v.collection.href} className="v2-focus">{t4}{t5}{t6}</Link>;
    $[9] = t4;
    $[10] = v.collection.href;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t7) {
    t8 = <p className={t1}>{t2}:{" "}{t7}</p>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

/** Who owns it — their profile when there is a handle or an id. */
export function NftOwner(t0) {
  const $ = _c(23);
  const {
    v,
    size: t1
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  let t2;
  let t3;
  if ($[0] !== size || $[1] !== v.lang || $[2] !== v.owner.avatar || $[3] !== v.owner.display) {
    const t = tr(v.lang === "fa");
    if ($[6] !== size || $[7] !== v.owner.avatar || $[8] !== v.owner.display) {
      t3 = <Avatar name={v.owner.display} src={v.owner.avatar} size={size} />;
      $[6] = size;
      $[7] = v.owner.avatar;
      $[8] = v.owner.display;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t2 = t("\u0645\u0627\u0644\u06A9", "Owned by");
    $[0] = size;
    $[1] = v.lang;
    $[2] = v.owner.avatar;
    $[3] = v.owner.display;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[10] !== t2) {
    t4 = <small>{t2}</small>;
    $[10] = t2;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== v.owner.display) {
    t5 = <b><bdi>{v.owner.display}</bdi></b>;
    $[12] = v.owner.display;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <span>{t4}{t5}</span>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t3 || $[18] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const body = t7;
  let t8;
  if ($[20] !== body || $[21] !== v.owner.href) {
    t8 = v.owner.href ? <Link href={v.owner.href} className="ap-seller v2-focus">{body}</Link> : <span className="ap-seller">{body}</span>;
    $[20] = body;
    $[21] = v.owner.href;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}

/** Its traits — each one a door to the collection's items with the same trait, when both halves are known. On a phone they scroll sideways. */
export function NftTraits(t0) {
  const $ = _c(19);
  const {
    v,
    fold
  } = t0;
  const isFa = v.lang === "fa";
  let T0;
  let list;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== isFa || $[1] !== v.lang || $[2] !== v.traits) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const t = tr(isFa);
      if (v.traits.length === 0) {
        t5 = null;
        break bb0;
      }
      const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
      list = <ul className="ns-traits">{v.traits.map((tr$0, i) => {
          const inner = <><small>{tr$0.type || t("\u0648\u06CC\u0698\u06AF\u06CC", "Trait")}</small><b><bdi>{tr$0.value || "-"}</bdi></b>{tr$0.percent ? <em>{withPercent(new Intl.NumberFormat(v.lang === "fa" ? "fa-IR" : "en-US", {
                maximumFractionDigits: 2
              }).format(Number(tr$0.percent)), v.lang === "fa")}</em> : null}</>;
          return <li key={`${tr$0.type}-${tr$0.value}-${i}`}>{tr$0.href ? <Link href={tr$0.href} className="ns-trait v2-focus">{inner}</Link> : <span className="ns-trait">{inner}</span>}</li>;
        })}</ul>;
      T0 = Panel;
      t1 = "ns-traits-h";
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <Sparkles aria-hidden={true} />;
        $[10] = t2;
      } else {
        t2 = $[10];
      }
      t3 = t("\u0648\u06CC\u0698\u06AF\u06CC\u200C\u0647\u0627", "Traits");
      t4 = nf.format(v.traits.length);
    }
    $[0] = isFa;
    $[1] = v.lang;
    $[2] = v.traits;
    $[3] = T0;
    $[4] = list;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    T0 = $[3];
    list = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[11] !== T0 || $[12] !== fold || $[13] !== list || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t6 = <T0 id={t1} icon={t2} title={t3} count={t4} fold={fold}>{list}</T0>;
    $[11] = T0;
    $[12] = fold;
    $[13] = list;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

/**
 * The piece's record — the legacy details, every row: the contract (whole
 * where it fits, pressed to copy), standard, token, chain, owner and the
 * creator's resale share. A value the payload does not carry reads «—»,
 * never a confident zero.
 */
export function NftRecordPanel(t0) {
  const $ = _c(19);
  const {
    v,
    fold
  } = t0;
  const isFa = v.lang === "fa";
  const t = tr(isFa);
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US", {
    maximumFractionDigits: 2
  });
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== v.contract) {
    t1 = async () => {
      try {
        await navigator.clipboard.writeText(v.contract);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {}
    };
    $[0] = v.contract;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copy = t1;
  let t2;
  if ($[2] !== v.standard) {
    t2 = v.standard ? <bdi>{v.standard}</bdi> : "\u2014";
    $[2] = v.standard;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== v.owner.address || $[5] !== v.owner.display || $[6] !== v.owner.name) {
    t3 = v.owner.name ? <bdi>{v.owner.name}</bdi> : v.owner.address ? <FitHex value={v.owner.address} /> : v.owner.display;
    $[4] = v.owner.address;
    $[5] = v.owner.display;
    $[6] = v.owner.name;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const cells = [[t("\u0627\u0633\u062A\u0627\u0646\u062F\u0627\u0631\u062F", "Standard"), t2], ...(v.tokenId != null ? [[t("\u0634\u0646\u0627\u0633\u0647\u200C\u06CC \u062A\u0648\u06A9\u0646", "Token ID"), <bdi key="i">#{v.tokenId}</bdi>] as [string, React.ReactNode]] : []), [t("\u0634\u0628\u06A9\u0647", "Chain"), v.network.display || "\u2014"], [t("\u0645\u0627\u0644\u06A9", "Owner"), t3], [t("\u0633\u0647\u0645 \u0633\u0627\u0632\u0646\u062F\u0647 \u0627\u0632 \u0641\u0631\u0648\u0634 \u0628\u0639\u062F\u06CC", "Creator's resale share"), v.royaltyPercent !== null ? withPercent(nf.format(v.royaltyPercent), v.lang === "fa") : "\u2014"]];
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FileText aria-hidden={true} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = "button";
  const t6 = "as-copy v2-focus";
  const t7 = `${t("\u06A9\u067E\u06CC \u0622\u062F\u0631\u0633 \u0642\u0631\u0627\u0631\u062F\u0627\u062F", "Copy the contract address")}: ${v.contract}`;
  let t8;
  if ($[9] !== v.contract) {
    t8 = <FitHex value={v.contract} className="as-hex" />;
    $[9] = v.contract;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== copied) {
    t9 = copied ? <Check aria-hidden={true} /> : <Copy aria-hidden={true} />;
    $[11] = copied;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== copy || $[14] !== t7 || $[15] !== t8 || $[16] !== t9 || $[17] !== v.contract) {
    t10 = <button type={t5} className={t6} onClick={copy} title={v.contract} aria-label={t7}>{t8}{t9}</button>;
    $[13] = copy;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = v.contract;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return <Panel id="ns-record-h" icon={t4} title={t("\u0634\u0646\u0627\u0633\u0646\u0627\u0645\u0647\u200C\u06CC \u0627\u062B\u0631", "About this piece")} fold={fold}><div className="ap-contract"><span>{t("\u0622\u062F\u0631\u0633 \u0642\u0631\u0627\u0631\u062F\u0627\u062F", "Contract address")}</span>{t10}</div><dl className="ap-cells">{cells.map(_temp)}</dl></Panel>;
}
function _temp(t0) {
  const [k, val] = t0;
  return <div key={k}><dt>{k}</dt><dd>{val}</dd></div>;
}